import "./TimeLogsList.scss";

import React from "react";
import { Link } from "react-router-dom";
import request from "request";
import usesFeatures from "usesFeatures";
import { ApplicationContext } from "ApplicationContext";
import moment from "moment-timezone";
import User from "User";

import Table from "Table";
import Header from "Header";
import Paginate from "Paginate";
import Input from "Input";
import Select from "Select";
import DatePicker from "DatePicker";
import {
  venueTimeZone, tzAbbr, formatInZone, isoToPickerDate, pickerValueToIso,
} from "lib/venue_time_zone";

class TimeLogsList extends React.Component {
  static contextType = ApplicationContext;

  state = {
    search: "",
    storeId: "",
    clockedInFrom: "",
    clockedInTo: "",
    sortBy: "start_at",
    sortOrder: "desc",
    currentPage: 1,
    downloading: false,
  };

  componentDidMount() {
    this.fetchTimeLogs(1);
  }

  buildURL = () => {
    const { search, storeId, clockedInFrom, clockedInTo, sortBy, sortOrder } = this.state;

    const params = new URLSearchParams({
      sort_by: sortBy,
      sort_order: sortOrder,
    });

    if (search) params.append("search", search);
    if (storeId) params.append("store_id", storeId);
    // clockedInFrom / clockedInTo are stored as UTC ISO strings by the picker
    // onChange handlers; pass through unchanged.
    if (clockedInFrom) params.append("clocked_in_from", clockedInFrom);
    if (clockedInTo) params.append("clocked_in_to", clockedInTo);

    return `venue_admin/time_logs?${params.toString()}`;
  };

  fetchTimeLogs = (page = 1) => {
    // Use fetchData instead of getPage so the response's `stores` and `venue`
    // (plus pagy/pageData) make it into props. hasPagination's getPage drops
    // every key besides pagy/pageData, which would leave the store dropdown
    // empty after the initial mount.
    const url = this.buildURL();
    const sep = url.indexOf("?") > -1 ? "&" : "?";
    this.props.fetchData(`${url}${sep}page=${page}`);
  };

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  // The text search debounces by ~350ms so we don't fire a request on every
  // keystroke. The other filters trigger immediately on change because their
  // values are picked, not typed. Clearing the input still fires a refetch
  // — the empty value drops the `search` param in buildURL.
  handleSearchChange = (value) => {
    this.setState({ search: value });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.fetchTimeLogs(1), 350);
  };

  handleFilterChange = (field, value) => {
    this.setState({ [field]: value }, () => {
      this.fetchTimeLogs(1);
    });
  };

  handleDownload = () => {
    const { search, storeId, clockedInFrom, clockedInTo } = this.state;

    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (storeId) params.append("store_id", storeId);
    if (clockedInFrom) params.append("clocked_in_from", clockedInFrom);
    if (clockedInTo) params.append("clocked_in_to", clockedInTo);

    const query = params.toString();
    const url = `/api/venue_admin/time_logs/download${query ? `?${query}` : ""}`;

    this.setState({ downloading: true });

    const authToken = User.getAuthToken();
    const csrfToken = document.getElementsByName("csrf-token")[0].content;

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Authorization", authToken);
    xhr.setRequestHeader("X-CSRF-Token", csrfToken);
    xhr.setRequestHeader("X-CURRENT-PAGE", window.location.href);
    xhr.responseType = "blob";

    xhr.onload = () => {
      this.setState({ downloading: false });
      if (xhr.status === 200) {
        const blob = xhr.response;
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `Time Logs - ${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        console.error("Download failed with status:", xhr.status);
      }
    };

    xhr.onerror = () => {
      this.setState({ downloading: false });
      console.error("XHR error occurred");
    };

    xhr.send();
  };

  handleSort = (column) => {
    const { sortBy, sortOrder } = this.state;
    const newOrder = sortBy === column && sortOrder === "desc" ? "asc" : "desc";
    this.setState({ sortBy: column, sortOrder: newOrder }, () => {
      this.fetchTimeLogs(this.state.currentPage);
    });
  };

  emptyTable = () => {
    return (
      <h3 className="is-size-6 is-italic">
        No Time Logs Found.
      </h3>
    );
  };

  formatDuration = (startAt, endAt) => {
    if (!endAt) return <span className="tag is-success">Active</span>;

    const start = moment(startAt);
    const end = moment(endAt);
    const duration = moment.duration(end.diff(start));

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    return `${hours}h ${minutes}m`;
  };

  render() {
    const { pageData, pagy, venue, stores } = this.props;
    const { search, storeId, clockedInFrom, clockedInTo } = this.state;
    const tz = venueTimeZone(this.context?.CurrentUserContext?.venue);
    const zone = tzAbbr(tz);
    const storeOptions = { "": "All Stores" };
    (stores || []).forEach((s) => { storeOptions[s.id] = s.name; });

    if (!pageData) {
      return (
        <div className="TimeLogsList">
          <Header text="Time Logs" />
          <div className="box">
            <p>Loading...</p>
          </div>
        </div>
      );
    }

    const FORMATTERS = {
      employee: (tl) => (
        <Link to={`/employees/${tl.employee?.id}/edit`} target="_blank">
          {tl.employee?.name || 'Unknown'}
        </Link>
      ),
      store: (tl) => {
        const storeName = tl.terminal?.store?.name;
        const number = tl.terminal?.number;
        if (storeName && number != null) return `${storeName} — Terminal #${number}`;
        if (storeName) return storeName;
        if (number != null) return `Terminal #${number}`;
        return <span className="has-text-grey-light">—</span>;
      },
      startAt: (tl) => formatInZone(tl.startAt, tz, "MMM D, YYYY h:mm A z"),
      endAt: (tl) => tl.endAt
        ? formatInZone(tl.endAt, tz, "MMM D, YYYY h:mm A z")
        : <span className="tag is-success">Active</span>,
      duration: (tl) => this.formatDuration(tl.startAt, tl.endAt),
      requestIpIn: (tl) => tl.requestIpIn || <span className="has-text-grey-light">—</span>,
      requestIpOut: (tl) => tl.requestIpOut || <span className="has-text-grey-light">—</span>,
      view: (tl) => (
        <span>
          <Link to={`/time_logs/${tl.id}`} className="mr-2">View</Link>
          <Link to={`/time_logs/${tl.id}/edit`}>Edit</Link>
        </span>
      ),
    };

    const HEADERS = {
      employee: "Employee",
      store: "Store",
      startAt: "Clock In",
      endAt: "Clock Out",
      duration: "Duration",
      requestIpIn: "IP In",
      requestIpOut: "IP Out",
      view: "",
    };

    const CLASSES = {
      view: "is-narrow",
      duration: "is-narrow",
    };

    return (
      <div className="TimeLogsList">
        <Header text={venue ? `Time Logs at ${venue.name}` : "Time Logs"}>
          <button
            className="button mr-2"
            onClick={this.handleDownload}
            disabled={this.state.downloading}
          >
            {this.state.downloading ? "Downloading..." : "Download CSV"}
          </button>
          <Link to="/time_logs/new" className="button is-primary">New Time Log</Link>
        </Header>

        <div className="box">
          <div className="columns is-multiline">
            <div className="column is-4">
              <Input
                label="Search by Name or IP"
                value={search}
                onChange={this.handleSearchChange}
                placeholder="Employee name or IP address..."
              />
            </div>

            <div className="column is-3">
              <Select
                label="Store"
                value={storeId}
                options={storeOptions}
                onChange={(value) => this.handleFilterChange("storeId", value)}
              />
            </div>

            <div className="column is-2">
              <DatePicker
                label={`Clock In From (${zone})`}
                input_type="datetime"
                value={isoToPickerDate(clockedInFrom, tz)}
                onChange={(v) => this.handleFilterChange("clockedInFrom", pickerValueToIso(v, tz))}
              />
            </div>

            <div className="column is-2">
              <DatePicker
                label={`Clock In To (${zone})`}
                input_type="datetime"
                value={isoToPickerDate(clockedInTo, tz)}
                onChange={(v) => this.handleFilterChange("clockedInTo", pickerValueToIso(v, tz))}
              />
            </div>
          </div>
        </div>

        <div className="block">
          <Table
            empty={this.emptyTable()}
            showHeader={true}
            headers={HEADERS}
            formatters={FORMATTERS}
            tdClasses={CLASSES}
            rows={pageData || []}
          />

          <Paginate
            changePage={this.fetchTimeLogs}
            {...this.props.pagy}
          />
        </div>
      </div>
    );
  }
}

export default usesFeatures(["data"], TimeLogsList);
